/**
 * GPU-resource ownership (q-p2triage-fe-gpu-dispose-ownership, H-1349).
 *
 * The rule: a placed CLONE never frees a SHARED resource; the owner of the shared
 * resource frees it once, when nothing it hands out is still live.
 *
 * Why it needs saying: `AssetLoader.instantiate*()` returns `clone(true)` (or the
 * skeleton-aware clone) of a cached template, and both share the template's
 * geometry and materials. The old disposer freed whatever it found under a
 * despawned clone — i.e. the template's buffers — so the next spawn of the same
 * asset re-uploaded them (a hitch per spawn), while the templates themselves (and
 * their textures and skeletons) were never freed at all when a cache cleared, so
 * GPU memory grew across zone changes.
 *
 * Mechanism: a module registry of shared resources. `AssetLoader` registers each
 * template as it caches it (`markShared`) and frees it on `clear()`
 * (`releaseShared`); module-level shared primitives (the NPC quest marker) register
 * the same way. `disposeObject3D` skips anything registered, and frees what a clone
 * does own — its own geometries/materials (e.g. tinted copies) and its own skeleton
 * (`SkeletonUtils.clone` gives every skinned clone a fresh `Skeleton`, whose bone
 * texture is per instance).
 *
 * Textures are freed only by `releaseShared`: a template owns its glTF textures
 * outright, whereas a texture found on a non-template material may come from a
 * module cache (the shrine glow, the omen icons) that outlives the mesh.
 *
 * Node-safe and duck-typed: the `three` import is type-only, and the tests' plain
 * stubs (no `traverse`, no `dispose`) pass through untouched.
 */

import type { Object3D } from 'three';

interface Disposable {
  dispose?: () => void;
}

const shared = new WeakSet<object>();

function isObject(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
}

/** The textures a material references: map slots plus shader-material uniforms. */
function texturesOf(material: unknown): object[] {
  if (!isObject(material)) return [];
  const found: object[] = [];
  for (const value of Object.values(material)) {
    if (isObject(value) && (value as { isTexture?: boolean }).isTexture === true) found.push(value);
  }
  const uniforms = (material as { uniforms?: Record<string, { value?: unknown }> }).uniforms;
  if (isObject(uniforms)) {
    for (const uniform of Object.values(uniforms)) {
      const value = uniform?.value;
      if (isObject(value) && (value as { isTexture?: boolean }).isTexture === true) {
        found.push(value);
      }
    }
  }
  return found;
}

interface Resources {
  owned: object[]; // geometries, materials, skeletons
  textures: object[];
}

/** Every distinct GPU-backed resource under `root` (each listed once). */
function collect(root: Object3D): Resources {
  const seen = new Set<object>();
  const owned: object[] = [];
  const textures: object[] = [];
  const add = (into: object[], value: unknown): void => {
    if (!isObject(value) || seen.has(value)) return;
    seen.add(value);
    into.push(value);
  };
  if (typeof (root as { traverse?: unknown }).traverse !== 'function') return { owned, textures };
  root.traverse((node: Object3D) => {
    const mesh = node as unknown as { geometry?: unknown; material?: unknown; skeleton?: unknown };
    add(owned, mesh.geometry);
    for (const material of asArray(mesh.material)) {
      add(owned, material);
      for (const texture of texturesOf(material)) add(textures, texture);
    }
    add(owned, mesh.skeleton);
  });
  return { owned, textures };
}

function disposeOne(resource: object): void {
  const dispose = (resource as Disposable).dispose;
  if (typeof dispose === 'function') dispose.call(resource);
}

/** Register explicit resources (geometries, materials, textures, skeletons) as shared. */
export function markSharedResource(...resources: readonly unknown[]): void {
  for (const resource of resources) if (isObject(resource)) shared.add(resource);
}

/** Register every resource under a cached template as shared (its clones never free them). */
export function markShared(root: Object3D): void {
  const { owned, textures } = collect(root);
  for (const resource of owned) shared.add(resource);
  for (const texture of textures) shared.add(texture);
}

/** True when `resource` is registered as shared (tests + diagnostics). */
export function isSharedResource(resource: unknown): boolean {
  return isObject(resource) && shared.has(resource);
}

/**
 * The owner's release: unregister and free everything under a template —
 * geometries, materials, textures and skeletons. Call once, when no clone of it is
 * still drawn (a cache `clear()` on teardown or zone change).
 */
export function releaseShared(root: Object3D): void {
  const { owned, textures } = collect(root);
  for (const resource of [...owned, ...textures]) {
    shared.delete(resource);
    disposeOne(resource);
  }
}

/**
 * Free what a detached object OWNS: its geometries, materials and skeletons, minus
 * anything registered as shared (a template's, or a module primitive's). Textures
 * are left to their owner (see the module note).
 */
export function disposeObject3D(object: Object3D): void {
  for (const resource of collect(object).owned) {
    if (!shared.has(resource)) disposeOne(resource);
  }
}
